/**
 * ContactForm component for collecting user information before assessment
 * 
 * @see src/modules/assessment/models/User.ts
 */
import React, { useState } from 'react';
import { useAssessmentContext } from '../../context/AssessmentContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import * as apiService from '../../services/apiService';
import { User } from '../../models/User';

// Company Size options
const COMPANY_SIZES = [
  { value: '1-9', label: '1–9' },
  { value: '10-99', label: '10–99' },
  { value: '100-999', label: '100–999' },
  { value: '1000-9999', label: '1,000–9,999' },
  { value: '10000+', label: '10,000+' },
];

// Industry options
const INDUSTRIES = [
  { value: 'technology', label: 'Technology' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'finance', label: 'Finance & Banking' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'retail', label: 'Retail & Consumer Goods' },
  { value: 'government', label: 'Government & Public Sector' },
  { value: 'education', label: 'Education' },
  { value: 'consulting', label: 'Consulting & Professional Services' },
  { value: 'other', label: 'Other' }
];

interface ContactFormProps {
  onComplete: (createdUser: User) => void;
}

const ContactForm: React.FC<ContactFormProps> = ({ onComplete }) => {
  const { dispatch } = useAssessmentContext();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    jobTitle: '',
    companySize: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when field is changed
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle select change
  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when field is changed
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.company.trim()) newErrors.company = 'Company is required';
    if (!formData.industry) newErrors.industry = 'Industry is required';
    if (!formData.companySize) newErrors.companySize = 'Company size is required';
    if (!formData.jobTitle.trim()) newErrors.jobTitle = 'Job title is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Create user in the database via API
      const user = await apiService.createUser(formData);
      
      // Update context with the real user
      dispatch({ 
        type: 'SET_USER', 
        payload: user
      });
      
      // Pass user to next step
      onComplete(user);
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({
        form: error instanceof Error ? error.message : 'Failed to create user. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      <h2 className="text-2xl font-bold text-charcoal mb-4">Your Information</h2>
      <p className="text-charcoal-light mb-6">
        We'll use these details to prepare your custom assessment report and follow up with valuable insights.
      </p>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-charcoal">Full Name *</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="John Doe"
              className={errors.name ? 'border-red-500' : 'border-gray-200 focus:border-deepBlue'}
            />
            {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email" className="text-charcoal">Business Email *</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="john.doe@company.com"
              className={errors.email ? 'border-red-500' : 'border-gray-200 focus:border-deepBlue'}
            />
            {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-charcoal">Business Phone *</Label>
            <Input
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="(555) 123-4567"
              className={errors.phone ? 'border-red-500' : 'border-gray-200 focus:border-deepBlue'}
            />
            {errors.phone && <p className="text-red-500 text-sm">{errors.phone}</p>}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="company" className="text-charcoal">Company/Organization *</Label>
            <Input
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Acme Corporation"
              className={errors.company ? 'border-red-500' : 'border-gray-200 focus:border-deepBlue'}
            />
            {errors.company && <p className="text-red-500 text-sm">{errors.company}</p>}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="industry" className="text-charcoal">Industry *</Label>
            <Select
              value={formData.industry}
              onValueChange={(value) => handleSelectChange('industry', value)}
            >
              <SelectTrigger className="border-gray-200 focus:border-deepBlue">
                <SelectValue placeholder="Select industry" />
              </SelectTrigger>
              <SelectContent>
                {INDUSTRIES.map(industry => (
                  <SelectItem key={industry.value} value={industry.value}>
                    {industry.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.industry && <p className="text-red-500 text-sm">{errors.industry}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="companySize" className="text-charcoal">Company/Employee Size *</Label>
            <Select
              value={formData.companySize}
              onValueChange={(value) => handleSelectChange('companySize', value)}
            >
              <SelectTrigger className="border-gray-200 focus:border-deepBlue">
                <SelectValue placeholder="Select company size" />
              </SelectTrigger>
              <SelectContent>
                {COMPANY_SIZES.map(size => (
                  <SelectItem key={size.value} value={size.value}>
                    {size.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.companySize && <p className="text-red-500 text-sm">{errors.companySize}</p>}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="jobTitle" className="text-charcoal">Job Title *</Label>
            <Input
              id="jobTitle"
              name="jobTitle"
              value={formData.jobTitle}
              onChange={handleChange}
              placeholder="Project Manager"
              className={errors.jobTitle ? 'border-red-500' : 'border-gray-200 focus:border-deepBlue'}
            />
            {errors.jobTitle && <p className="text-red-500 text-sm">{errors.jobTitle}</p>}
          </div>
        </div>
        
        {errors.submit && (
          <div className="bg-red-50 p-3 rounded border border-red-200">
            <p className="text-red-500">{errors.submit}</p>
          </div>
        )}
        
        <div className="pt-2">
          <Button
            type="submit"
            className="w-full md:w-auto px-8 py-2.5 bg-deepBlue hover:bg-deepBlue-light text-white font-medium rounded-md transition-colors"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Begin Assessment'}
          </Button>
        </div>
        
        <p className="text-xs text-charcoal-light mt-2 italic">
          <span className="font-medium">Disclaimer:</span> This tool provides general insights and is not a substitute for a professional PMO assessment. Results are for informational use only.
        </p>
      </form>
    </div>
  );
};

export default ContactForm;
